/*globals google_api_key :readonly, countryIso :readonly, skyapi :readonly, siteLanguage :readonly */

import {setOptions, importLibrary} from "@googlemaps/js-api-loader"
import {ConvertExperienceService} from '../services/convert-experience-service';
import {CookieService} from '../services/cookie.service';
import {B2bGATracker, FormInteractionType, FormName} from './b2b-ga-tracker-service';
import {UsercentricsService} from './usercentrics-service';

type SelectedPlace = {
    label: string,
    lat: number,
    long: number,
} | null;

type formError = {
    message?: string,
    field: string,
    type: 'required' | 'invalid' | 'duplicate',
};

export class HomeProspectForm {
    private selectedPlace: SelectedPlace = null;
    private form: HTMLFormElement;
    private locationInput: HTMLInputElement;
    private duplicateCheckUrl: URL;
    private emailCheckUrl: URL;


    async activate(): void {
        this.form = document.getElementById('home-prospect-form') as HTMLFormElement;
        this.emailCheckUrl = new URL(this.form.getAttribute('data-email-validate-check-url'), window.location.origin);
        this.duplicateCheckUrl = new URL(this.form.getAttribute('data-email-duplicate-check-url'), window.location.origin);
        this.trackFirstInteraction();

        setOptions({
            key: google_api_key,
            language: siteLanguage
        });
        await importLibrary("places");
        this.locationInput = this.form.querySelector("input[name='location_input']") as HTMLInputElement;
        const autocompleteOptions = {
            fields: ["formatted_address", "geometry", "name"],
            strictBounds: false,
            componentRestrictions: {country: countryIso},
        };
        const autocomplete = new google.maps.places.Autocomplete(this.locationInput, autocompleteOptions);
        let place = null;

        autocomplete.addListener("place_changed", () => {
            place = autocomplete.getPlace();

            if (!place.geometry || !place.geometry.location) {
                this.selectedPlace = null;
                return;
            }
            this.selectedPlace = {
                label: place.formatted_address || place.name || "",
                lat: place.geometry.location.lat(),
                long: place.geometry.location.lng(),
            }
        });
    }

    private trackFirstInteraction(): void {
        this.form.addEventListener('focusin', () => {
            B2bGATracker.sendProspectFormInteractionEvent(FormInteractionType.VIEW);
        }, {once: true});
    }

    async submitForm(): void {
        this.setSubmitting(true);
        try {
            await this.checkForm();
        } catch (error) {
            this.setSubmitting(false);
            if (Array.isArray(error)) {
                B2bGATracker.sendProspectFormInteractionEvent(FormInteractionType.ERROR, {
                    errorData: (error as formError[]).map((e) => ({errorField: e.field, errorType: e.type})),
                });
            }
            return;
        }
        let url = `${skyapi as string}/companies/create-prospect-minimal`;
        try {
            let formData = {
                primaryCategory: parseInt((this.form.querySelector("*[name='category']") as HTMLSelectElement | HTMLInputElement).value),
                contactEmail: (this.form.querySelector("input[name='email']") as HTMLInputElement).value,
                promoCode: (this.form.querySelector("input[name='promoCode']") as HTMLInputElement).value,
                authcode: (this.form.querySelector("input[name='authcode']") as HTMLInputElement).value,
                location: this.selectedPlace,
                abTest: document.body.dataset.ab,
                convertExperiences: [],
                cookieConsent: this.getCookieConsentData(),
            }

            try {
                formData.convertExperiences = ConvertExperienceService.getAllExperiences().map(
                    (exp) => `${exp.experimentId}:${exp.variationId}`
                );
            } catch (error) {
                console.error("Failed to get convert experiences:", error);
            }

            formData = {...formData, ...this.getTrackingCookies()};

            const response = await fetch(url, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(formData),
            });

            if (!response.ok) {
                console.error("Failed to submit form:", response.statusText);
                this.setSubmitting(false);
                return;
            }

            const data = await response.json();

            B2bGATracker.sendProspectFormInteractionEvent(FormInteractionType.SUBMIT, {
                businessCategoryId: formData.primaryCategory,
            });
            await B2bGATracker.sendFormStartEvent(FormName.BusinessRegistration);

            if (data.redirect_url) {
                // Keep the button in its loading state — the page is navigating away.
                await this.redirectTo(data.redirect_url as string);
            } else {
                console.warn("No redirect URL found in response");
                this.setSubmitting(false);
            }
        } catch (error) {
            console.error("Error submitting form:", error);
            this.setSubmitting(false);
        }

    }

    private setSubmitting(state: boolean): void {
        const btn = document.getElementById("start-prospect-sign-up-flow") as HTMLButtonElement;
        if (!btn) {
            return;
        }
        const spinner = btn.querySelector(".spinner-border") as HTMLElement;
        if (!spinner) {
            return;
        }

        if (state) {
            btn.disabled = true;
            btn.classList.remove("btn-arrow");
            btn.classList.add("loading");
            spinner.classList.remove("d-none");
        } else {
            btn.disabled = false;
            btn.classList.add("btn-arrow");
            btn.classList.remove("loading");
            spinner.classList.add("d-none");
        }
    }

    private async redirectTo(url: string): Promise<void> {
        window.location.assign(await window.skyComponents.crossDomainLinkService.getCrossDomainUrl(url) as string);
    }

    private async checkForm(): Promise<void> {
        const errors: formError[] = [];
        this.form.querySelectorAll(".error-message").forEach(el => el.classList.add("d-none"));
        this.form.querySelectorAll(".error").forEach(el => el.classList.remove("error"));

        const categorySelect = this.form.querySelector<HTMLSelectElement>("select[name='category']");
        if (categorySelect && (!categorySelect.value || categorySelect.value === "")) {
            errors.push({ type: 'required' , field: 'category' });
        }

        if (this.selectedPlace === null) {
            errors.push({ type: 'required', field: 'location_input' });
        }

        const emailInput = this.form.querySelector<HTMLInputElement>("input[name='email']");
        const email = emailInput?.value.trim() ?? "";
        if (email === "") {
            errors.push({ type: 'required', field: 'email' });
        } else {
            try {
                await Promise.all([
                    this.checkEmailRemote(email).catch(() => {
                        throw { type: 'invalid' as const, field: 'email' };
                    }),
                    this.duplicateEmailCheck(email).catch(() => {
                        throw { type: 'duplicate' as const, field: 'email' };
                    }),
                ]);
            } catch (err) {
                errors.push(err as formError);
            }
        }

        if (errors.length > 0) {
            this.applyErrors(errors);
            throw errors;
        }
    }

    private applyErrors(errors: formError[]): void {
        errors.forEach(err => {
            const field = this.form.querySelector<HTMLElement>(`[name='${err.field}']`);
            if (!field) return;

            field.classList.add("error");

            const wrapper = field.closest(".form-field-wrapper");
            const errorContainer = wrapper?.querySelector<HTMLParagraphElement>(
                `.error-message[data-error='${err.type}']`
            );

            if (errorContainer) {
                errorContainer.classList.remove("d-none");
            }
        });
    }


    private async checkEmailRemote(email: string): Promise<void> {
        if (!email) {
            return Promise.reject();
        }

        const url = new URL(this.emailCheckUrl);
        url.searchParams.set('value', email);

        const delays = [100, 100];

        for (let attempt = 0; attempt <= delays.length; attempt++) {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Email check failed with status ${response.status}`);
                }
                const valid = await response.json() as boolean;
                if (!valid) {
                    return Promise.reject();
                }
                return Promise.resolve();
            } catch (error) {
                if (attempt === delays.length) {
                    // Quiet no-op on network failure: the server-side check still guards submission
                    console.warn('Email check failed after retries, skipping:', error);
                    return Promise.resolve();
                }
                await new Promise(resolve => setTimeout(resolve, delays[attempt]));
            }
        }
    }

    private async duplicateEmailCheck(email: string): Promise<void> {
        if (!email) {
            return;
        }

        const url = new URL(this.duplicateCheckUrl);
        url.searchParams.set('value', email);

        const delays = [100, 100];

        for (let attempt = 0; attempt <= delays.length; attempt++) {
            try {
                const response = await fetch(url);
                if (!response.ok) {
                    throw new Error(`Duplicate email check failed with status ${response.status}`);
                }
                const valid = await response.json() as boolean;
                if (!valid) {
                    return Promise.reject('Email is already in use');
                }
                return Promise.resolve();
            } catch (error) {
                if (attempt === delays.length) {
                    // Quiet no-op on network failure: the server-side check still guards submission
                    console.warn('Duplicate email check failed after retries, skipping:', error);
                    return Promise.resolve();
                }
                await new Promise(resolve => setTimeout(resolve, delays[attempt]));
            }
        }
    }

    private getTrackingCookies(): Record<string, string> {
        const cookieMap: Record<string, string> = {
            'b2b_utm_converted': 'b2bUtmConverted',
            'b2b_utm_original': 'b2bUtmOriginal',
            'b2c_utm_original': 'b2cUtmOriginal',
            'gclid': 'gclid',
            'gbraid': 'gbraid',
            'wbraid': 'wbraid',
            '_fbc': '_fbc',
            '_fbp': '_fbp',
        };

        const allCookies = CookieService.getAllCookies();
        const mappedCookies: Record<string, string> = {};

        Object.entries(cookieMap).forEach(([cookieName, targetKey]) => {
            const cookieValue = allCookies[cookieName];
            if (cookieValue) {
                mappedCookies[targetKey] = cookieValue;
            }
        });

        return mappedCookies;
    }

    private getCookieConsentData(): Record<string, boolean> {
        if (!window.skyComponents ||
            !window.skyComponents.usercentricsService ||
            !(window.skyComponents.usercentricsService instanceof UsercentricsService)
        ) {
            return {};
        }
        const usercentricsService: UsercentricsService = window.skyComponents.usercentricsService;

        if (!usercentricsService.isInitialized()) {
            console.warn('"usercentricsService" is not initialized.');
            return {};
        }

        return {
            'googleAds': usercentricsService.getCookieConsentData('Google Ads'),
            'facebookPixel': usercentricsService.getCookieConsentData('Facebook Pixel')
        };
    }
}

(<any>window).skyComponents = (<any>window).skyComponents || {};
(<any>window).skyComponents.homeProspectForm = new HomeProspectForm();
(<any>window).skyComponents.homeProspectForm.activate();
